import React, { useState, useCallback, useEffect, useRef } from 'react';
import FlowSimulator from '../components/FlowSimulator';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import API from '../services/api';

// Custom Node Components with Connection Handles
const SendMessageNode = ({ data, selected, id }) => (
  <div className={`node-base node-green ${selected ? 'node-selected node-green-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-green" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">📤 Send Message</div>
    <div className="node-content">{data.message || 'Click to edit message...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-green" />
  </div>
);

const WaitForReplyNode = ({ data, selected, id }) => {
  const choices = data.choices || ['Yes', 'No', 'Other'];
  // Dynamic width taaki text aur handles khule-khule dikhein
  const nodeMinWidth = Math.max(260, choices.length * 85);

  return (
    <div 
      className={`node-base node-blue ${selected ? 'node-selected node-blue-selected' : 'node-not-selected'}`} 
      style={{ 
        minWidth: `${nodeMinWidth}px`, 
        paddingBottom: '26px', // Niche space taaki dots border se chipke na
        position: 'relative'
      }}
    >
      {/* Top Input Handle */}
      <Handle type="target" position={Position.Top} className="handle-blue" />

      <button
        onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
        title="Delete node"
      >✕</button>

      <div className="node-title">⏳ Wait for Response</div>
      <div className="node-content" style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
        Variable: <strong>{data.variable || '@input'}</strong>
      </div>

      {/* ✅ SEPARATE HANDLES ROW: Har option ke neeche alag dot */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-around', 
        borderTop: '1px solid #e2e8f0', 
        paddingTop: '10px', 
        position: 'relative',
        width: '100%'
      }}>
        {choices.map((choice, idx) => {
          const cleanId = `choice-${choice.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
          const isLast = idx === choices.length - 1;

          return (
            <div 
              key={idx} 
              style={{ 
                textAlign: 'center', 
                fontSize: '11px', 
                fontWeight: 600, 
                color: '#1e3a5f', 
                position: 'relative',
                flex: 1,
                padding: '0 4px'
              }}
            >
              {/* Option Name */}
              <span style={{ 
                display: 'block', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap'
              }}>
                {choice}
              </span>

              {/* ✅ HAR OPTION KA APNA INDIVIDUAL DOT */}
              <Handle
                type="source"
                position={Position.Bottom}
                id={cleanId}
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bottom: '-28px', // Exact bottom border par
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: isLast ? '#94a3b8' : '#2563eb', // 'Other' grey hoga, baaki blue
                  border: '2px solid #ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  cursor: 'crosshair',
                  zIndex: 10
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ConditionNode = ({ data, selected, id }) => (
  <div className={`node-base node-orange ${selected ? 'node-selected node-orange-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-orange" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">🔀 Condition</div>
    <div className="node-content">{data.condition || 'Set condition...'}</div>
    <Handle type="source" position={Position.Bottom} id="true" className="handle-success" />
    <Handle type="source" position={Position.Bottom} id="false" className="handle-danger" />
  </div>
);

const ApiCallNode = ({ data, selected, id }) => (
  <div className={`node-base node-purple ${selected ? 'node-selected node-purple-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-purple" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">🔌 API Call</div>
    <div className="node-content-small">{data.url || 'Configure endpoint...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-purple" />
  </div>
);

const AddLabelsNode = ({ data, selected, id }) => (
  <div className={`node-base node-cyan ${selected ? 'node-selected node-cyan-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-cyan" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">🏷️ Add Labels</div>
    <div className="node-content">{data.labels || 'urgent, customer'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-cyan" />
  </div>
);

const UpdateContactNode = ({ data, selected, id }) => (
  <div className={`node-base node-teal ${selected ? 'node-selected node-teal-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-teal" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">👤 Update Contact</div>
    <div className="node-content">Set: {data.field || 'name'} = {data.value || '@input'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-teal" />
  </div>
);

const WaitForTimeNode = ({ data, selected, id }) => (
  <div className={`node-base node-violet ${selected ? 'node-selected node-violet-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-violet" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">⏰ Wait for Time</div>
    <div className="node-content">Delay: {data.duration || '1 hour'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-violet" />
  </div>
);

const SendBroadcastNode = ({ data, selected, id }) => (
  <div className={`node-base node-pink ${selected ? 'node-selected node-pink-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-pink" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">📢 Send Broadcast</div>
    <div className="node-content">To: {data.group || 'All contacts'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-pink" />
  </div>
);

const InteractiveMessageNode = ({ data, selected, id }) => (
  <div className={`node-base node-sky ${selected ? 'node-selected node-sky-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-sky" />

    <button
      onClick={(e) => {
        e.stopPropagation();
        data.onDelete?.(id);
      }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >
      ✕
    </button>

    <div className="node-title">🎯 Interactive Message</div>
    <div className="node-content">Buttons: {data.buttons || 'Yes, No, Maybe'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-sky" />
  </div>
);

const SendImageNode = ({ data, selected, id }) => (
  <div className={`node-base node-orange-light ${selected ? 'node-selected node-orange-light-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-orange-light" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">🖼️ Send Image</div>
    <div className="node-content">{data.imageUrl || 'Upload or paste URL...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-orange-light" />
  </div>
);

const SendDocumentNode = ({ data, selected, id }) => (
  <div className={`node-base node-lime ${selected ? 'node-selected node-lime-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-lime" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">📄 Send Document</div>
    <div className="node-content">{data.fileName || 'Upload PDF/File...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-lime" />
  </div>
);

const AssignToAgentNode = ({ data, selected, id }) => (
  <div className={`node-base node-indigo ${selected ? 'node-selected node-indigo-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-indigo" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">👨‍💼 Assign to Agent</div>
    <div className="node-content">Team: {data.team || 'Support Team'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-indigo" />
  </div>
);

const JumpToFlowNode = ({ data, selected, id }) => (
  <div className={`node-base node-emerald ${selected ? 'node-selected node-emerald-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-emerald" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">↪️ Jump to Flow</div>
    <div className="node-content">Target: {data.flowName || 'Select flow...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-emerald" />
  </div>
);

const CaptureLocationNode = ({ data, selected, id }) => (
  <div className={`node-base node-rose ${selected ? 'node-selected node-rose-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-rose" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">📍 Request Location</div>
    <div className="node-content">Save as: {data.variable || '@location'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-rose" />
  </div>
);

const SendTemplateNode = ({ data, selected, id }) => (
  <div className={`node-base node-fuchsia ${selected ? 'node-selected node-fuchsia-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-fuchsia" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">📋 WhatsApp Template</div>
    <div className="node-content">{data.templateName || 'Select template...'}</div>
    <Handle type="source" position={Position.Bottom} className="handle-fuchsia" />
  </div>
);

// Multi-Language Response Node
const MultiLanguageNode = ({ data, selected, id }) => (
  <div className={`node-base node-cyan ${selected ? 'node-selected node-cyan-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-cyan" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">🌐 Multi-Language</div>
    <div className="node-content-small">
      EN: {(data.messageEN || 'English...').substring(0, 20)}...
    </div>
    <Handle type="source" position={Position.Bottom} className="handle-cyan" />
  </div>
);

// Send Menu/Options Node
const SendMenuNode = ({ data, selected, id }) => (
  <div className={`node-base node-purple ${selected ? 'node-selected node-purple-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-purple" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">📋 Send Menu</div>
    <div className="node-content">
      {data.menuTitle || 'Menu Title'}
    </div>
    <Handle type="source" position={Position.Bottom} className="handle-purple" />
  </div>
);

// Context-Aware Response Node
const ContextResponseNode = ({ data, selected, id }) => (
  <div className={`node-base node-teal ${selected ? 'node-selected node-teal-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-teal" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">🔄 Context Response</div>
    <div className="node-content">
      Context: {data.contextKey || '@previous_choice'}
    </div>
    <Handle type="source" position={Position.Bottom} className="handle-teal" />
  </div>
);

// Time-Based Greeting Node
const TimeBasedGreetingNode = ({ data, selected, id }) => (
  <div className={`node-base node-orange ${selected ? 'node-selected node-orange-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-orange" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">⏰ Time Greeting</div>
    <div className="node-content">
      Morning/Evening/Night
    </div>
    <Handle type="source" position={Position.Bottom} className="handle-orange" />
  </div>
);

// Smart Escalation Node
const SmartEscalationNode = ({ data, selected, id }) => (
  <div className={`node-base node-red ${selected ? 'node-selected node-red-selected' : 'node-not-selected'}`}>
    <Handle type="target" position={Position.Top} className="handle-red" />
    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>
    <div className="node-title">🚨 Smart Escalation</div>
    <div className="node-content">
      Route to: {data.department || 'Auto-detect'}
    </div>
    <Handle type="source" position={Position.Bottom} className="handle-red" />
  </div>
);

const AIAssistantNode = ({ data, selected, id }) => (
  <div className={`node-base ${selected ? 'node-selected' : 'node-not-selected'}`}
    style={{
      background: 'linear-gradient(135deg, #1e3a5f, #2d5986)',
      color: 'white',
      minWidth: '220px',
      paddingBottom: '30px',
      position: 'relative'
    }}>
    <Handle type="target" position={Position.Top} style={{ background: '#1e3a5f' }} />

    <button
      onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
      className={`node-delete-btn ${selected ? 'node-delete-btn-visible' : ''}`}
      title="Delete node"
    >✕</button>

    <div className="node-title" style={{ color: 'white' }}>🤖 AI Assistant</div>
    <div className="node-content" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>
      {data.assistantName || 'No assistant selected'}
    </div>

    {/* Two output handles: done and handoff — no handle for "continue" (stays on node) */}
    <div style={{
      display: 'flex', justifyContent: 'space-around',
      borderTop: '1px solid rgba(255,255,255,0.2)',
      paddingTop: '10px', marginTop: '8px', position: 'relative'
    }}>
      <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, position: 'relative', flex: 1 }}>
        <span>✅ Done</span>
        <Handle
          type="source" position={Position.Bottom} id="done"
          style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            bottom: '-28px', width: '12px', height: '12px', borderRadius: '50%',
            background: '#22c55e', border: '2px solid #ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'crosshair', zIndex: 10
          }}
        />
      </div>
      <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, position: 'relative', flex: 1 }}>
        <span>🤝 Handoff</span>
        <Handle
          type="source" position={Position.Bottom} id="handoff"
          style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            bottom: '-28px', width: '12px', height: '12px', borderRadius: '50%',
            background: '#f59e0b', border: '2px solid #ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'crosshair', zIndex: 10
          }}
        />
      </div>
    </div>
  </div>
);

const nodeTypes = {
  sendMessage: SendMessageNode,
  waitForReply: WaitForReplyNode,
  condition: ConditionNode,
  apiCall: ApiCallNode,
  addLabels: AddLabelsNode,
  updateContact: UpdateContactNode,
  waitForTime: WaitForTimeNode,
  sendBroadcast: SendBroadcastNode,
  interactiveMessage: InteractiveMessageNode,
  sendImage: SendImageNode,
  sendDocument: SendDocumentNode,
  assignToAgent: AssignToAgentNode,
  jumpToFlow: JumpToFlowNode,
  captureLocation: CaptureLocationNode,
  sendTemplate: SendTemplateNode,
  multiLanguage: MultiLanguageNode,
  sendMenu: SendMenuNode,
  contextResponse: ContextResponseNode,
  timeBasedGreeting: TimeBasedGreetingNode,
  smartEscalation: SmartEscalationNode,
  aiAssistant: AIAssistantNode, 
};

function FlowBuilderInner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState('Untitled Flow');
  const [flowDescription, setFlowDescription] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedNodes, setCopiedNodes] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [aiAssistantsList, setAiAssistantsList] = useState([]);

useEffect(() => {
  API.get('/ai-assistants')
    .then(res => setAiAssistantsList(res.data.data || []))
    .catch(() => {});
}, []);
  const reactFlowInstance = useReactFlow();

  useEffect(() => {
    if (id) {
      loadFlow(id);
    } else {
      // Start node with proper handles
      setNodes([
        {
          id: 'start',
          type: 'input',
          data: { label: '🚀 FLOW START' },
          position: { x: 400, y: 50 },
          style: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: '3px solid #5a67d8',
            borderRadius: '12px',
            padding: '16px 24px',
            fontWeight: 'bold',
            fontSize: '16px',
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
          },
          sourcePosition: Position.Bottom,
        }
      ]);
    }
  }, [id]);

  const loadFlow = async (flowId) => {
    try {
      const response = await API.get(`/flows/${flowId}`);
      const flow = response.data.data;
      setFlowName(flow.name);
      setFlowDescription(flow.description);

      const flowData = typeof flow.flow_data === 'string'
        ? JSON.parse(flow.flow_data)
        : flow.flow_data;

      // Naya
      if (flowData && flowData.nodes) {
        let nodesToLoad = flowData.nodes;

        // Agar start node nahi hai nodes mein, add karo
        const hasStartNode = flowData.nodes.find(n => n.id === 'start');
        if (!hasStartNode) {
          nodesToLoad = [
            {
              id: 'start',
              type: 'input',
              data: { label: '🚀 FLOW START' },
              position: { x: 400, y: 50 },
              style: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: '3px solid #5a67d8',
                borderRadius: '12px',
                padding: '16px 24px',
                fontWeight: 'bold',
                fontSize: '16px',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
              },
              sourcePosition: Position.Bottom,
            },
            ...flowData.nodes
          ];
        }

        const nodesWithDelete = nodesToLoad.map(node => ({
          ...node,
          data: { ...node.data, onDelete: deleteNode }
        }));
        setNodes(nodesWithDelete);
        setEdges(flowData.edges || []);
      }
      toast.success('Flow loaded successfully!');
    } catch (error) {
      toast.error('Failed to load flow');
      console.error(error);
    }
  };
const [isPreviewOpen, setIsPreviewOpen] = useState(false); // ✅ Preview toggle state (Zaroori tha)
  const connectingNodeId = useRef(null);
  const connectingHandleId = useRef(null);

  const onConnectStart = useCallback((_, { nodeId, handleId }) => {
    connectingNodeId.current = nodeId;
    connectingHandleId.current = handleId;
  }, []);

  const onConnectEnd = useCallback((event) => {
    if (!connectingNodeId.current) return;

    const targetIsPane = event.target.classList.contains('react-flow__pane');
    if (targetIsPane) {
      const { clientX, clientY } = event;

      const position = reactFlowInstance.screenToFlowPosition
        ? reactFlowInstance.screenToFlowPosition({ x: clientX, y: clientY })
        : reactFlowInstance.project({
            x: clientX - 60,
            y: clientY - 114
          });

      const newNodeId = `node-${Date.now()}`;
      const sourceId = connectingNodeId.current;
      const sourceHandleId = connectingHandleId.current;

      const newNode = {
        id: newNodeId,
        type: 'sendMessage',
        position,
        data: {
          ...getDefaultNodeData('sendMessage'),
          onDelete: deleteNode
        }
      };

      // 1. Naya node add karo
      setNodes((nds) => nds.concat(newNode));

      // 2. Wire/Edge ko fixed ID ke saath add karo taaki delete na ho
      setTimeout(() => {
        setEdges((eds) => addEdge({
          id: `edge-${sourceId}-${newNodeId}-${Date.now()}`,
          source: sourceId,
          sourceHandle: sourceHandleId || null,
          target: newNodeId,
          animated: true,
          type: 'smoothstep',
          style: { stroke: '#667eea', strokeWidth: 3 }
        }, eds));
      }, 50);
    }

    connectingNodeId.current = null;
    connectingHandleId.current = null;
  }, [reactFlowInstance, setNodes, setEdges]);
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: '#667eea', strokeWidth: 3 },
      type: 'smoothstep',
      selected: false
    }, eds)),
    [setEdges]
  );

  const defaultEdgeOptions = {
    style: {
      stroke: '#667eea',
      strokeWidth: 3
    },
    type: 'smoothstep',
    animated: true,
    selectedStyle: {
      stroke: '#ef4444',
      strokeWidth: 4
    }
  };

  const deleteNode = (nodeId) => {
    if (nodeId === 'start') {
      toast.error('Cannot delete start node!');
      return;
    }
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    toast.success('Node deleted');
  };
  const autoArrange = () => {
    const NODE_WIDTH = 240;
    const NODE_HEIGHT = 130;
    const H_GAP = 40;
    const V_GAP = 60;

    // Build graph
    const childrenMap = {};
    const parentMap = {};
    nodes.forEach(n => { childrenMap[n.id] = []; parentMap[n.id] = []; });
    edges.forEach(e => {
      if (childrenMap[e.source]) childrenMap[e.source].push(e.target);
      if (parentMap[e.target]) parentMap[e.target].push(e.source);
    });

    // Find roots
    const roots = nodes
      .filter(n => n.id === 'start' || parentMap[n.id]?.length === 0)
      .map(n => n.id);
    if (roots.length === 0 && nodes.length > 0) roots.push(nodes[0].id);

    // Assign depth via BFS
    const depth = {};
    const visited = new Set();
    const queue = roots.map(r => ({ id: r, d: 0 }));
    while (queue.length > 0) {
      const { id: nid, d } = queue.shift();
      if (visited.has(nid)) continue;
      visited.add(nid);
      depth[nid] = d;
      (childrenMap[nid] || []).forEach(c => {
        if (!visited.has(c)) queue.push({ id: c, d: d + 1 });
      });
    }
    // Disconnected nodes get appended at end
    let maxD = Object.values(depth).length > 0 ? Math.max(...Object.values(depth)) : 0;
    nodes.forEach(n => {
      if (depth[n.id] === undefined) { maxD += 1; depth[n.id] = maxD; }
    });

    // Group by depth
    const byDepth = {};
    nodes.forEach(n => {
      const d = depth[n.id];
      if (!byDepth[d]) byDepth[d] = [];
      byDepth[d].push(n.id);
    });

    // Calculate subtree width for each node (post-order)
    const subtreeWidth = {};
    const allDepths = Object.keys(byDepth).map(Number).sort((a, b) => b - a);

    allDepths.forEach(d => {
      byDepth[d].forEach(nid => {
        const kids = (childrenMap[nid] || []).filter(c => depth[c] > d);
        if (kids.length === 0) {
          subtreeWidth[nid] = NODE_WIDTH;
        } else {
          const total = kids.reduce((sum, c) => sum + subtreeWidth[c], 0)
            + (kids.length - 1) * H_GAP;
          subtreeWidth[nid] = Math.max(NODE_WIDTH, total);
        }
      });
    });

    // Assign x positions top-down
    const posX = {};
    const posY = {};

    // Start roots centered
    const totalRootWidth = roots.reduce((s, r) => s + subtreeWidth[r], 0)
      + (roots.length - 1) * H_GAP;
    let rootStartX = -totalRootWidth / 2;

    roots.forEach(r => {
      posX[r] = rootStartX + subtreeWidth[r] / 2;
      posY[r] = 0;
      rootStartX += subtreeWidth[r] + H_GAP;
    });

    // BFS to assign children positions
    const posQueue = [...roots];
    const posVisited = new Set(roots);

    while (posQueue.length > 0) {
      const nid = posQueue.shift();
      const kids = (childrenMap[nid] || []).filter(c => depth[c] > depth[nid] && !posVisited.has(c));
      if (kids.length === 0) continue;

      const totalKidWidth = kids.reduce((s, c) => s + subtreeWidth[c], 0)
        + (kids.length - 1) * H_GAP;
      let kidX = posX[nid] - totalKidWidth / 2;

      kids.forEach(c => {
        posX[c] = kidX + subtreeWidth[c] / 2;
        posY[c] = depth[c] * (NODE_HEIGHT + V_GAP);
        kidX += subtreeWidth[c] + H_GAP;
        posVisited.add(c);
        posQueue.push(c);
      });
    }

    // Disconnected nodes — place at bottom
    let disconnectedX = 0;
    nodes.forEach(n => {
      if (posX[n.id] === undefined) {
        posX[n.id] = disconnectedX;
        posY[n.id] = (maxD + 1) * (NODE_HEIGHT + V_GAP);
        disconnectedX += NODE_WIDTH + H_GAP;
      }
    });

    // Apply
    setNodes(nds => nds.map(n => ({
      ...n,
      position: {
        x: (posX[n.id] ?? 0) + 800,
        y: (posY[n.id] ?? 0) + 60
      }
    })));

    toast.success('✨ Flow arranged!');
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.12, duration: 700 });
    }, 150);
  };
  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };
  const addNode = (type) => {
    const { x: viewportX, y: viewportY, zoom } = reactFlowInstance.getViewport();
    const centerX = (-viewportX + window.innerWidth / 2) / zoom;
    const centerY = (-viewportY + window.innerHeight / 2) / zoom;

    const newNode = {
      id: `node-${Date.now()}`,
      type: type,
      position: {
        x: centerX - 110,
        y: centerY - 50
      },
      data: getDefaultNodeData(type),
    };

    setNodes((nds) => {
      const updatedNode = {
        ...newNode,
        data: { ...newNode.data, onDelete: deleteNode }
      };
      return nds.concat(updatedNode);
    });

    toast.success(`${getNodeLabel(type)} added!`);
  };

  const getNodeLabel = (type) => {
    const labels = {
      sendMessage: 'Send Message',
      waitForReply: 'Wait for Reply',
      condition: 'Condition',
      apiCall: 'API Call',
      addLabels: 'Add Labels',
      updateContact: 'Update Contact',
      waitForTime: 'Wait for Time',
      sendBroadcast: 'Send Broadcast',
      interactiveMessage: 'Interactive Message',
      sendImage: 'Send Image',
      sendDocument: 'Send Document',
      assignToAgent: 'Assign to Agent',
      jumpToFlow: 'Jump to Flow',
      captureLocation: 'Capture Location',
      sendTemplate: 'WhatsApp Template',
      multiLanguage: 'Multi-Language',
      sendMenu: 'Send Menu',
      contextResponse: 'Context Response',
      timeBasedGreeting: 'Time Greeting',
      smartEscalation: 'Smart Escalation',
      aiAssistant: 'AI Assistant'
    };
    return labels[type] || type;
  };
  const exportFlow = () => {
    const flowData = {
      nodes: nodes.filter(n => n.id !== 'start'),
      edges: edges,
      metadata: {
        name: flowName,
        description: flowDescription,
        exportedAt: new Date().toISOString()
      }
    };

    const blob = new Blob([JSON.stringify(flowData, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${flowName.replace(/\s+/g, '_')}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success(`Flow exported: ${flowData.nodes.length} nodes`);
  };

  const importFlow = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const flowData = JSON.parse(e.target.result);

        if (!flowData.nodes || !Array.isArray(flowData.nodes)) {
          throw new Error('Invalid flow format');
        }

        const importedNodes = flowData.nodes.map(node => ({
          ...node,
          id: `node-${Date.now()}-${Math.random()}`,
          position: {
            x: node.position.x + 50,
            y: node.position.y + 50
          },
          data: { ...node.data, onDelete: deleteNode }
        }));

        const idMap = {};
        flowData.nodes.forEach((oldNode, index) => {
          idMap[oldNode.id] = importedNodes[index].id;
        });

        const importedEdges = flowData.edges.map(edge => ({
          ...edge,
          id: `edge-${Date.now()}-${Math.random()}`,
          source: idMap[edge.source] || edge.source,
          target: idMap[edge.target] || edge.target
        }));

        setNodes((nds) => [...nds, ...importedNodes]);
        setEdges((eds) => [...eds, ...importedEdges]);

        toast.success(`Imported ${importedNodes.length} nodes!`);
        event.target.value = '';
      } catch (error) {
        toast.error('Invalid flow file!');
        console.error('Import error:', error);
      }
    };

    reader.readAsText(file);
  };

  const getDefaultNodeData = (type) => {
    switch (type) {
      case 'sendMessage':
        return { message: 'Hello! How can I help you today?' };
      case 'waitForReply':
        return { variable: '@input', timeout: '1 hour', choices: ['Yes', 'No', 'Other'] };
      case 'condition':
        return { condition: '@input has "yes"' };
      case 'apiCall':
        return { url: 'https://api.example.com/endpoint', method: 'GET' };
      case 'addLabels':
        return { labels: 'customer, urgent' };
      case 'updateContact':
        return { field: 'name', value: '@input' };
      case 'waitForTime':
        return { duration: '1 hour' };
      case 'sendBroadcast':
        return { group: 'All contacts', message: 'Important announcement!' };
      case 'interactiveMessage':
        return { text: 'Choose an option:', buttons: 'Yes, No, Maybe' };
      case 'sendImage':
        return { imageUrl: '', caption: 'Check this out!' };
      case 'sendDocument':
        return { fileName: '', fileUrl: '', caption: '' };
      case 'assignToAgent':
        return { team: 'Support Team', priority: 'normal' };
      case 'jumpToFlow':
        return { flowId: '', flowName: 'Select flow' };
      case 'captureLocation':
        return { variable: '@location', message: 'Please share your location' };
      case 'sendTemplate':
        return { templateName: '', templateId: '', parameters: '' };
      case 'multiLanguage':
        return {
          messageEN: 'Hello! How can I help?',
          messageHI: 'Namaste! Main aapki kaise madad kar sakta hoon?',
          detectLanguage: true
        };
      case 'sendMenu':
        return {
          menuTitle: 'Please choose:',
          options: [
            { id: 1, label: 'Option 1', emoji: '1️⃣' },
            { id: 2, label: 'Option 2', emoji: '2️⃣' },
            { id: 3, label: 'Option 3', emoji: '3️⃣' }
          ]
        };
      case 'contextResponse':
        return {
          contextKey: '@previous_choice',
          responses: {
            'services': 'Service-specific response',
            'products': 'Product-specific response'
          }
        };
      case 'timeBasedGreeting':
        return {
          morningMsg: 'Good morning! 🌅',
          afternoonMsg: 'Good afternoon! ☀️',
          eveningMsg: 'Good evening! 🌆',
          nightMsg: 'Thanks for reaching out! 🌙 Our team is offline...'
        };
     case 'smartEscalation':
  return {
    department: '',
    phone: '',
    message: ''
  };

case 'aiAssistant':
  return { assistantId: '', assistantName: '' };

default:
  return { label: 'New Node' };
    }
  };

  const saveFlow = async () => {
    if (!flowName.trim()) {
      toast.error('Please enter a flow name');
      return;
    }

    // ✅ Subscription check
    const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
    const hasSubscription = localStorage.getItem('subscriptionStatus') === 'active';

    if (!isSuperAdmin && !hasSubscription) {
      toast.error('Active subscription required!');
      setTimeout(() => navigate('/pricing'), 1500);
      return;
    }

    setIsSaving(true);
    const loadingToast = toast.loading('Saving flow...');

    try {
      // Naya
      const hasStartNode = nodes.find(n => n.id === 'start');
      const nodesToSave = hasStartNode ? nodes : [
        {
          id: 'start',
          type: 'input',
          data: { label: '🚀 FLOW START' },
          position: { x: 400, y: 50 },
          sourcePosition: 'bottom'
        },
        ...nodes
      ];

      const flowData = {
        name: flowName,
        description: flowDescription,
        flowData: { nodes: nodesToSave, edges }
      };

      if (id) {
        await API.put(`/flows/${id}`, flowData);
        toast.success('Flow updated successfully!', { id: loadingToast });
      } else {
        await API.post('/flows', flowData);
        toast.success('Flow created successfully!', { id: loadingToast });
      }

      setTimeout(() => navigate('/flows'), 1000);
    } catch (error) {
      toast.error('Failed to save flow', { id: loadingToast });
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const onNodeClick = (event, node) => {
    const latestNode = nodes.find(n => n.id === node.id);
    setSelectedNode(latestNode || node);
  };

  const updateNodeData = (nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              ...newData,
              onDelete: deleteNode
            }
          };
        }
        return node;
      })
    );

    setSelectedNode(prev => {
      if (prev && prev.id === nodeId) {
        return {
          ...prev,
          data: { ...prev.data, ...newData, onDelete: deleteNode }
        };
      }
      return prev;
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isTyping =
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable;

      if (isTyping) return;

      // Ctrl+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        const nodesToCopy = selectedNodes.length > 0
          ? nodes.filter(n => selectedNodes.includes(n.id))
          : selectedNode ? [nodes.find(n => n.id === selectedNode.id)] : [];

        if (nodesToCopy.length > 0) {
          setCopiedNodes(nodesToCopy);
          toast.success(`${nodesToCopy.length} node(s) copied!`);
        }
      }

      // Ctrl+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        if (copiedNodes.length > 0) {
          const { x: viewportX, y: viewportY, zoom } = reactFlowInstance.getViewport();
          const pasteX = (-viewportX + window.innerWidth / 2) / zoom;
          const pasteY = (-viewportY + window.innerHeight / 2) / zoom;

          const newNodes = copiedNodes.map((node, index) => ({
            ...node,
            id: `node-${Date.now()}-${index}`,
            position: {
              x: pasteX + (index * 20),
              y: pasteY + (index * 20)
            },
            data: { ...node.data, onDelete: deleteNode }
          }));

          setNodes((nds) => [...nds, ...newNodes]);
          toast.success(`${newNodes.length} node(s) pasted!`);
        }
      }

      // Ctrl+A - Select All
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        const allNodeIds = nodes.filter(n => n.id !== 'start').map(n => n.id);
        setSelectedNodes(allNodeIds);
        toast.success(`${allNodeIds.length} nodes selected!`);
      }

      // Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (selectedNodes.length > 0) {
          selectedNodes.forEach(nodeId => {
            if (nodeId !== 'start') deleteNode(nodeId);
          });
          setSelectedNodes([]);
        } else if (selectedNode && selectedNode.id !== 'start') {
          deleteNode(selectedNode.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, selectedNodes, copiedNodes, nodes, reactFlowInstance]);

  useEffect(() => {
    const handleEdgeDelete = (e) => {
      const isTyping =
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable;

      if (isTyping) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        // ReactFlow automatically handles edge deletion when edge is selected
      }
    };

    window.addEventListener('keydown', handleEdgeDelete);
    return () => window.removeEventListener('keydown', handleEdgeDelete);
  }, []);

  return (
    <div className="flow-container" style={
      isFullscreen
        ? { position: 'fixed', inset: 0, zIndex: 99999, background: 'white', paddingTop: 0, paddingLeft: 0 }
        : { paddingTop: '64px', paddingLeft: '60px' }
    }>
      {/* Top Bar */}
      {!isFullscreen && <div className="flow-topbar">

        <div className="flow-topbar-left">
          <button onClick={() => navigate('/flows')} className="flow-back-btn">
            ← Back
          </button>
          <input
            type="text"
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            placeholder="Flow Name"
            className="flow-name-input"
          />

          {/* Export/Import Buttons */}
          <button
            onClick={exportFlow}
            className="flow-action-btn flow-export-btn"
            title="Export flow to JSON"
          >
            📥 Export
          </button>
          <button
            onClick={autoArrange}
            className="flow-action-btn"
            title="Auto-arrange all nodes"
            style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white' }}
          >
            ✨ Auto Arrange
          </button>

          <button
            type="button"
            onClick={() => setIsPreviewOpen(!isPreviewOpen)}
            className="flow-action-btn"
            title="Live WhatsApp Preview"
            style={{
              background: isPreviewOpen ? '#059669' : '#1e3a5f',
              color: 'white',
              fontWeight: '700'
            }}
          >
            {isPreviewOpen ? '✕ Close Preview' : '📱 Live Preview'}
          </button>
          <label
            htmlFor="import-flow"
            className="flow-action-btn flow-import-btn"
            title="Import flow from JSON"
          >
            📤 Import
            <input
              type="file"
              id="import-flow"
              accept=".json"
              onChange={importFlow}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        <button
          onClick={saveFlow}
          disabled={isSaving}
          className="flow-save-btn"
        >
          {isSaving ? '💾 Saving...' : '💾 Save Flow'}
        </button>
      </div>
      }

      <div className="flow-content" style={{ marginTop: '50px' }}>

        {/* Left Sidebar */}
        {!isFullscreen && <div className="flow-sidebar">

          <h3 className="flow-sidebar-title">📦 Add Nodes</h3>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">MESSAGING</div>
            <button onClick={() => addNode('sendMessage')} className="flow-node-btn" style={{ background: '#22c55e' }}>
              📤 Send Message
            </button>
            <button onClick={() => addNode('interactiveMessage')} className="flow-node-btn" style={{ background: '#0ea5e9' }}>
              🎯 Interactive Message
            </button>
            <button onClick={() => addNode('sendBroadcast')} className="flow-node-btn" style={{ background: '#ec4899' }}>
              📢 Send Broadcast
            </button>
          </div>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">MEDIA & FILES</div>
            <button onClick={() => addNode('sendImage')} className="flow-node-btn" style={{ background: '#f97316' }}>
              🖼️ Send Image
            </button>
            <button onClick={() => addNode('sendDocument')} className="flow-node-btn" style={{ background: '#84cc16' }}>
              📄 Send Document
            </button>
            <button onClick={() => addNode('sendTemplate')} className="flow-node-btn" style={{ background: '#d946ef' }}>
              📋 WhatsApp Template
            </button>
          </div>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">ADVANCED</div>
            <button onClick={() => addNode('assignToAgent')} className="flow-node-btn" style={{ background: '#6366f1' }}>
              👨‍💼 Assign to Agent
            </button>
            <button onClick={() => addNode('jumpToFlow')} className="flow-node-btn" style={{ background: '#14b8a6' }}>
              ↪️ Jump to Flow
            </button>
            <button onClick={() => addNode('captureLocation')} className="flow-node-btn" style={{ background: '#f43f5e' }}>
              📍 Request Location
            </button>
          </div>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">FLOW CONTROL</div>
            <button onClick={() => addNode('waitForReply')} className="flow-node-btn" style={{ background: '#3b82f6' }}>
              ⏳ Wait for Reply
            </button>
            <button onClick={() => addNode('condition')} className="flow-node-btn" style={{ background: '#f59e0b' }}>
              🔀 Condition
            </button>
            <button onClick={() => addNode('waitForTime')} className="flow-node-btn" style={{ background: '#a855f7' }}>
              ⏰ Wait for Time
            </button>
          </div>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">ACTIONS</div>
            <button onClick={() => addNode('updateContact')} className="flow-node-btn" style={{ background: '#10b981' }}>
              👤 Update Contact
            </button>
            <button onClick={() => addNode('addLabels')} className="flow-node-btn" style={{ background: '#06b6d4' }}>
              🏷️ Add Labels
            </button>
            <button onClick={() => addNode('apiCall')} className="flow-node-btn" style={{ background: '#8b5cf6' }}>
              🔌 API Call
            </button>
          </div>

          <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">SMART FEATURES ⭐</div>
            <button onClick={() => addNode('multiLanguage')} className="flow-node-btn" style={{ background: '#06b6d4' }}>
              🌐 Multi-Language
            </button>
            <button onClick={() => addNode('sendMenu')} className="flow-node-btn" style={{ background: '#8b5cf6' }}>
              📋 Send Menu
            </button>
            <button onClick={() => addNode('contextResponse')} className="flow-node-btn" style={{ background: '#10b981' }}>
              🔄 Context Response
            </button>
            <button onClick={() => addNode('timeBasedGreeting')} className="flow-node-btn" style={{ background: '#f59e0b' }}>
              ⏰ Time Greeting
            </button>
            <button onClick={() => addNode('smartEscalation')} className="flow-node-btn" style={{ background: '#ef4444' }}>
              🚨 Smart Escalation
            </button>
          </div>

                    <div className="flow-sidebar-section">
            <div className="flow-sidebar-section-title">AI 🤖</div>
            <button onClick={() => addNode('aiAssistant')} className="flow-node-btn" style={{ background: '#1e3a5f' }}>
              🤖 AI Assistant
            </button>
          </div>

          <div className="flow-tips-box">
            <div className="flow-tips-title">💡 Quick Tips</div>
            <ul className="flow-tips-list">
              <li>Drag nodes to position them</li>
              <li>Connect from bottom to top circles</li>
              <li>Click node to edit details</li>
              <li>Press Delete to remove node</li>
            </ul>
          </div>
        </div>
        }

        {/* Canvas */}
        <div className="flow-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            elementsSelectable={true}
            selectNodesOnDrag={true}
            minZoom={0.05}
            maxZoom={2}
            onSelectionChange={useCallback((params) => {
              const ids = params.nodes.map(n => n.id);
              setSelectedNodes(ids);
            }, [])}
            multiSelectionKeyCode="Control"
          >
            <Controls style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }} />


            {/* Fullscreen Button */}
            <MiniMap
              style={{
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '8px'
              }}
              nodeColor={(node) => {
                const colors = {
                  sendMessage: '#22c55e',
                  waitForReply: '#3b82f6',
                  condition: '#f59e0b',
                  apiCall: '#8b5cf6',
                  addLabels: '#06b6d4',
                  updateContact: '#10b981',
                  waitForTime: '#a855f7',
                  sendBroadcast: '#ec4899',
                  interactiveMessage: '#0ea5e9',
                  sendImage: '#f97316',
                  sendDocument: '#84cc16',
                  assignToAgent: '#6366f1',
                  jumpToFlow: '#14b8a6',
                  captureLocation: '#f43f5e',
                  sendTemplate: '#d946ef'
                };
                return colors[node.type] || '#667eea';
              }}
            />
            <Background variant={BackgroundVariant.Dots} gap={20} size={1.5} color="#d1d5db" />
          </ReactFlow>

          {/* ✅ Floating WhatsApp Live Simulator */}
          {isPreviewOpen && (
            <FlowSimulator
              nodes={nodes}
              edges={edges}
              onClose={() => setIsPreviewOpen(false)}
            />
          )}

          {/* ✅ Fullscreen icon - canvas ke top-right corner mein */}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 100,
              width: '32px',
              height: '32px',
              background: 'white',
              border: '1.5px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
              color: '#374151'
            }}
          >
            {isFullscreen ? '🗗' : '⛶'}
          </button>
        </div>

        {/* Right Sidebar - Node Properties */}
        {selectedNode && selectedNode.id !== 'start' && (
          <div className="flow-properties">
            <div className="flow-properties-header">
              <h3 className="flow-properties-title">⚙️ Node Settings</h3>
              <button onClick={() => setSelectedNode(null)} className="flow-properties-close">
                ✕
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Node Type (Change Type)</label>
              <select
                className="form-select"
                value={selectedNode.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  const newDefaults = getDefaultNodeData(newType);
                  setNodes((nds) => nds.map((n) => {
                    if (n.id === selectedNode.id) {
                      return {
                        ...n,
                        type: newType,
                        data: { ...newDefaults, onDelete: deleteNode }
                      };
                    }
                    return n;
                  }));
                  setSelectedNode((prev) => ({
                    ...prev,
                    type: newType,
                    data: { ...newDefaults, onDelete: deleteNode }
                  }));
                }}
              >
                <option value="sendMessage">📤 Send Message</option>
                <option value="waitForReply">⏳ Wait for Response (with choices)</option>
                <option value="interactiveMessage">🎯 Interactive Message (Buttons)</option>
                <option value="sendImage">🖼️ Send Image</option>
                <option value="sendDocument">📄 Send Document</option>
                <option value="updateContact">👤 Update Contact</option>
                <option value="condition">🔀 Condition</option>
                <option value="apiCall">🔌 API Call</option>
                <option value="sendMenu">📋 Send Menu</option>
                <option value="multiLanguage">🌐 Multi-Language</option>
                <option value="timeBasedGreeting">⏰ Time Greeting</option>
                <option value="smartEscalation">🚨 Smart Escalation</option>
                <option value="aiAssistant">🤖 AI Assistant</option>
              </select>
            </div>

            {/* Dynamic Fields Based on Node Type */}
            {selectedNode.type === 'sendMessage' && (
              <div className="form-group">
                <label className="form-label">Message Text</label>
                <textarea
                  value={selectedNode.data.message || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                  rows={5}
                  placeholder="Enter your message here..."
                  className="form-textarea"
                />
              </div>
            )}

            {/* ✅ IS UPDATE KIYE HUE BLOCK SE REPLACE KAREIN: */}
            {selectedNode.type === 'waitForReply' && (
              <>
                <div className="form-group">
                  <label className="form-label">Save Response As Variable</label>
                  <input
                    type="text"
                    value={selectedNode.data.variable || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { variable: e.target.value })}
                    placeholder="@user_choice"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Timeout Duration</label>
                  <input
                    type="text"
                    value={selectedNode.data.timeout || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { timeout: e.target.value })}
                    placeholder="30 minutes"
                    className="form-input"
                  />
                </div>

                {/* Dynamic Choice Management */}
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Branches / Choices ({(selectedNode.data.choices || []).length})</label>
                    <button
                      type="button"
                      onClick={() => {
                        const current = selectedNode.data.choices || ['Yes', 'No'];
                        const newOptionName = `Option ${current.length + 1}`;
                        updateNodeData(selectedNode.id, { choices: [...current, newOptionName] });
                      }}
                      style={{
                        padding: '3px 8px',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      + Add Choice
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(selectedNode.data.choices || ['Yes', 'No', 'Other']).map((choice, cIdx) => (
                      <div key={cIdx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={choice}
                          onChange={(e) => {
                            const updated = [...(selectedNode.data.choices || [])];
                            updated[cIdx] = e.target.value;
                            updateNodeData(selectedNode.id, { choices: updated });
                          }}
                          className="form-input"
                          style={{ fontSize: '12px', padding: '6px 10px' }}
                        />
                        {(selectedNode.data.choices || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (selectedNode.data.choices || []).filter((_, i) => i !== cIdx);
                              updateNodeData(selectedNode.id, { choices: updated });
                            }}
                            style={{
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              color: '#dc2626',
                              borderRadius: '4px',
                              padding: '6px 8px',
                              cursor: 'pointer'
                            }}
                            title="Remove choice"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '6px', display: 'block' }}>
                    💡 Har choice ka canvas par ek separate connection handle banega.
                  </small>
                </div>
              </>
            )}

            {selectedNode.type === 'condition' && (
              <div className="form-group">
                <label className="form-label">Condition Expression</label>
                <input
                  type="text"
                  value={selectedNode.data.condition || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { condition: e.target.value })}
                  placeholder='@input has "keyword"'
                  className="form-input"
                />
                <div className="flow-condition-help">
                  Examples: @input == "yes", @input has "help"
                </div>
              </div>
            )}

            {selectedNode.type === 'apiCall' && (
              <>
                <div className="form-group">
                  <label className="form-label">API URL</label>
                  <input
                    type="text"
                    value={selectedNode.data.url || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { url: e.target.value })}
                    placeholder="https://api.example.com/data"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Method</label>
                  <select
                    value={selectedNode.data.method || 'GET'}
                    onChange={(e) => updateNodeData(selectedNode.id, { method: e.target.value })}
                    className="form-select"
                  >
                    <option>GET</option>
                    <option>POST</option>
                    <option>PUT</option>
                    <option>DELETE</option>
                  </select>
                </div>
              </>
            )}

            {selectedNode.type === 'addLabels' && (
              <div className="form-group">
                <label className="form-label">Labels (comma-separated)</label>
                <input
                  type="text"
                  value={selectedNode.data.labels || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { labels: e.target.value })}
                  placeholder="urgent, customer, vip"
                  className="form-input"
                />
              </div>
            )}

            {selectedNode.type === 'updateContact' && (
              <>
                <div className="form-group">
                  <label className="form-label">Select What to Update</label>
                  <select
                    value={selectedNode.data.field || 'name'}
                    onChange={(e) => updateNodeData(selectedNode.id, { field: e.target.value })}
                    className="form-select"
                  >
                    <option value="name">Name</option>
                    <option value="email">Email</option>
                    <option value="address">Address</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Value (Variable or Text)</label>
                  <input
                    type="text"
                    value={selectedNode.data.value || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { value: e.target.value })}
                    placeholder="@contact_name or @input"
                    className="form-input"
                  />
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    💡 Tip: Wait for Response node ka variable name yahan daalein (e.g. <code>@contact_name</code>).
                  </small>
                </div>
              </>
            )}

            {selectedNode.type === 'waitForTime' && (
              <div className="form-group">
                <label className="form-label">Wait Duration</label>
                <input
                  type="text"
                  value={selectedNode.data.duration || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { duration: e.target.value })}
                  placeholder="1 hour, 30 minutes, 2 days"
                  className="form-input"
                />
              </div>
            )}

            {selectedNode.type === 'sendBroadcast' && (
              <>
                <div className="form-group">
                  <label className="form-label">Target Group</label>
                  <input
                    type="text"
                    value={selectedNode.data.group || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { group: e.target.value })}
                    placeholder="All contacts, VIP customers"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message</label>
                  <textarea
                    value={selectedNode.data.message || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                    rows={4}
                    placeholder="Broadcast message..."
                    className="form-textarea"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'interactiveMessage' && (
              <>
                <div className="form-group">
                  <label className="form-label">Message Text</label>
                  <textarea
                    value={selectedNode.data.text || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { text: e.target.value })}
                    rows={3}
                    placeholder="Choose an option:"
                    className="form-textarea"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Button Labels (comma-separated)</label>
                  <input
                    type="text"
                    value={selectedNode.data.buttons || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { buttons: e.target.value })}
                    placeholder="Yes, No, Maybe"
                    className="form-input"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'sendImage' && (
              <>
                {/* ✅ NEW: Image Source Toggle */}
                <div className="form-group">
                  <label className="form-label">Image Source</label>
                  <select
                    value={selectedNode.data.imageSource || 'url'}
                    onChange={(e) => updateNodeData(selectedNode.id, {
                      imageSource: e.target.value,
                      imageUrl: '',
                      uploadedImagePath: ''
                    })}
                    className="form-select"
                  >
                    <option value="url">Image URL</option>
                    <option value="upload">Upload Image</option>
                  </select>
                </div>

                {/* Show URL input if source is 'url' */}
                {(!selectedNode.data.imageSource || selectedNode.data.imageSource === 'url') && (
                  <div className="form-group">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      value={selectedNode.data.imageUrl || ''}
                      onChange={(e) => updateNodeData(selectedNode.id, { imageUrl: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="form-input"
                    />
                  </div>
                )}

                {/* ✅ NEW: Upload input if source is 'upload' */}
                {selectedNode.data.imageSource === 'upload' && (
                  <div className="form-group">
                    <label className="form-label">Upload Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const uploadToast = toast.loading('Uploading image...');

                        try {
                          // ✅ Create local preview URL first
                          const localPreviewUrl = URL.createObjectURL(file);

                          // ✅ Show immediate preview
                          updateNodeData(selectedNode.id, {
                            localPreview: localPreviewUrl,
                            uploadedImagePath: null,
                            imageUrl: null
                          });

                          const formData = new FormData();
                          formData.append('image', file);

                          const response = await API.post('/flows/upload-image', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });

                          const uploadedPath = response.data.data.imagePath;

                          // ✅ For live server, use full server URL
                          const isLive = window.location.hostname !== 'localhost';
                          const imageUrl = isLive
                            ? `http://128.199.30.125${uploadedPath}`
                            : `http://localhost:5000${uploadedPath}`;

                          // ✅ Update with server URL but KEEP showing image
                          updateNodeData(selectedNode.id, {
                            uploadedImagePath: uploadedPath,
                            imageUrl: imageUrl,
                            localPreview: imageUrl // ✅ Keep preview showing
                          });

                          console.log('✅ Image uploaded:', imageUrl);
                          toast.success('Image uploaded!', { id: uploadToast });

                        } catch (error) {
                          toast.error('Upload failed!', { id: uploadToast });
                          console.error('Upload error:', error);
                        }
                      }}
                      className="form-input"
                      style={{ padding: '10px' }}
                    />

                    {/* ✅ IMPROVED PREVIEW BOX */}
                    {(selectedNode.data.localPreview || selectedNode.data.uploadedImagePath) && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        border: '2px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#475569',
                          marginBottom: '8px'
                        }}>
                          🖼️ Preview:
                        </div>

                        <div style={{
                          position: 'relative',
                          display: 'inline-block',
                          maxWidth: '100%'
                        }}>
                          <img
                            src={selectedNode.data.localPreview || selectedNode.data.imageUrl}
                            alt="Preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '180px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              display: 'block'
                            }}
                          />

                          {/* Upload status indicator */}
                          {selectedNode.data.localPreview && !selectedNode.data.uploadedImagePath && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'rgba(245, 158, 11, 0.9)',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              ⏳ Uploading...
                            </div>
                          )}

                          {selectedNode.data.uploadedImagePath && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'rgba(16, 185, 129, 0.9)',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              ✅ Uploaded
                            </div>
                          )}
                        </div>

                        {selectedNode.data.uploadedImagePath && (
                          <p style={{
                            fontSize: '11px',
                            color: '#10b981',
                            marginTop: '8px',
                            marginBottom: '0'
                          }}>
                            ✅ Image ready to use in flow
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Caption (same for both) */}
                <div className="form-group">
                  <label className="form-label">Caption (Optional)</label>
                  <input
                    type="text"
                    value={selectedNode.data.caption || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { caption: e.target.value })}
                    placeholder="Image caption..."
                    className="form-input"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'sendDocument' && (
              <>
                <div className="form-group">
                  <label className="form-label">File Name</label>
                  <input
                    type="text"
                    value={selectedNode.data.fileName || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { fileName: e.target.value })}
                    placeholder="document.pdf"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">File URL</label>
                  <input
                    type="text"
                    value={selectedNode.data.fileUrl || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { fileUrl: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                    className="form-input"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'assignToAgent' && (
              <>
                <div className="form-group">
                  <label className="form-label">Team Name</label>
                  <input
                    type="text"
                    value={selectedNode.data.team || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { team: e.target.value })}
                    placeholder="Support Team"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    value={selectedNode.data.priority || 'normal'}
                    onChange={(e) => updateNodeData(selectedNode.id, { priority: e.target.value })}
                    className="form-select"
                  >
                    <option>low</option>
                    <option>normal</option>
                    <option>high</option>
                    <option>urgent</option>
                  </select>
                </div>
              </>
            )}

            {selectedNode.type === 'jumpToFlow' && (
              <div className="form-group">
                <label className="form-label">Flow Name</label>
                <input
                  type="text"
                  value={selectedNode.data.flowName || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { flowName: e.target.value })}
                  placeholder="Enter flow name"
                  className="form-input"
                />
              </div>
            )}

            {selectedNode.type === 'captureLocation' && (
              <>
                <div className="form-group">
                  <label className="form-label">Request Message</label>
                  <input
                    type="text"
                    value={selectedNode.data.message || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
                    placeholder="Please share your location"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Save As Variable</label>
                  <input
                    type="text"
                    value={selectedNode.data.variable || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { variable: e.target.value })}
                    placeholder="@location"
                    className="form-input"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'sendTemplate' && (
              <>
                <div className="form-group">
                  <label className="form-label">Template Name</label>
                  <input
                    type="text"
                    value={selectedNode.data.templateName || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { templateName: e.target.value })}
                    placeholder="welcome_message"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Parameters (comma-separated)</label>
                  <input
                    type="text"
                    value={selectedNode.data.parameters || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { parameters: e.target.value })}
                    placeholder="@contact.name, @order.id"
                    className="form-input"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'multiLanguage' && (
              <>
                <div className="form-group">
                  <label className="form-label">English Message</label>
                  <textarea
                    value={selectedNode.data.messageEN || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { messageEN: e.target.value })}
                    rows={3}
                    placeholder="English text..."
                    className="form-textarea"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Hindi Message</label>
                  <textarea
                    value={selectedNode.data.messageHI || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { messageHI: e.target.value })}
                    rows={3}
                    placeholder="Hindi text..."
                    className="form-textarea"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'sendMenu' && (
              <>
                <div className="form-group">
                  <label className="form-label">Menu Title</label>
                  <input
                    type="text"
                    value={selectedNode.data.menuTitle || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { menuTitle: e.target.value })}
                    placeholder="Please choose:"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Options (one per line)</label>
                  <textarea
                    value={selectedNode.data.optionsText || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { optionsText: e.target.value })}
                    rows={5}
                    placeholder="1️⃣ Our Services\n2️⃣ Our Products\n3️⃣ Request Demo"
                    className="form-textarea"
                  />
                </div>
              </>
            )}

            {selectedNode.type === 'smartEscalation' && (
              <>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select
                    value={selectedNode.data.department || 'auto'}
                    onChange={(e) => updateNodeData(selectedNode.id, { department: e.target.value })}
                    className="form-select"
                  >
                    <option value="auto">Auto-Detect</option>
                    <option value="ceo">CEO - Vinod Kumar</option>
                    <option value="app_dev">App Developer - Aryan/Abhishek</option>
                    <option value="web_dev">Web Developer - Md Dilshad</option>
                    <option value="security">Security - Ajitesh</option>
                    <option value="ai_ml">AI/ML - Ravi</option>
                  </select>
                </div>
              </>
            )}
                        {selectedNode.type === 'aiAssistant' && (
              <>
                <div className="form-group">
                  <label className="form-label">Select AI Assistant</label>
                  <select
                    value={selectedNode.data.assistantId || ''}
                    onChange={(e) => {
                      const selected = aiAssistantsList.find(a => a.id === parseInt(e.target.value));
                      updateNodeData(selectedNode.id, {
                        assistantId: e.target.value ? parseInt(e.target.value) : '',
                        assistantName: selected ? selected.name : ''
                      });
                    }}
                    className="form-select"
                  >
                    <option value="">-- Select an assistant --</option>
                    {aiAssistantsList.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.model})</option>
                    ))}
                  </select>
                  <small style={{ color: '#64748b', fontSize: '11px', marginTop: '6px', display: 'block' }}>
                    💡 No assistant here? Go to <strong>AI Assistants</strong> in the sidebar to create one first.
                  </small>
                </div>

                <div style={{
  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
  padding: '10px 12px', fontSize: '12px', color: '#1e40af', marginTop: '10px'
}}>
  🌐 <strong>Global AI:</strong> Do not connect this node to anything. On any button/menu step of the flow, if the user types anything outside the options, this AI will reply, and then the same menu will appear again. Only connect the <strong>🤝 Handoff</strong> handle when the user wants to talk to a human.
</div>
```[cite: 6]
              </>
            )}

            {selectedNode.type === 'timeBasedGreeting' && (
              <>
                <div className="form-group">
                  <label className="form-label">Morning Message (5 AM - 12 PM)</label>
                  <input
                    type="text"
                    value={selectedNode.data.morningMsg || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { morningMsg: e.target.value })}
                    placeholder="Good morning! 🌅"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Afternoon Message (12 PM - 5 PM)</label>
                  <input
                    type="text"
                    value={selectedNode.data.afternoonMsg || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { afternoonMsg: e.target.value })}
                    placeholder="Good afternoon! ☀️"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Evening Message (5 PM - 10 PM)</label>
                  <input
                    type="text"
                    value={selectedNode.data.eveningMsg || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { eveningMsg: e.target.value })}
                    placeholder="Good evening! 🌆"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Night Message (10 PM - 5 AM)</label>
                  <textarea
                    value={selectedNode.data.nightMsg || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { nightMsg: e.target.value })}
                    rows={3}
                    placeholder="Thanks for reaching out! 🌙 Our team is offline..."
                    className="form-textarea"
                  />
                </div>
              </>
            )}

            <button onClick={() => deleteNode(selectedNode.id)} className="flow-delete-node-btn">
              🗑️ Delete Node
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Wrapper component with ReactFlowProvider
function FlowBuilder() {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner />
    </ReactFlowProvider>
  );
}

export default FlowBuilder;